import { DESKTOP_THRESHOLD } from '../../../config/constants';
import type { BannersData } from '../../../services/ui';

import { lobbyBannerHasAnimation } from './bannerAnimation';
import { getRiveBackgroundUrl } from './rive/utils';

/**
 * The lobby banner <img> is the LCP element for most sessions, and ~79% of LCP is
 * resourceLoadDelay — the image cannot start downloading until React has rendered the <img>,
 * which does not happen until after asyncWithLDProvider and root.render(). Injecting a preload
 * link as soon as the banner payload arrives lets the browser fetch it *concurrently* with LD
 * init, React mount and store construction instead of strictly after all of them.
 *
 * Worst case if the URL guessed here is not the one rendered: one unused image request. The
 * page is never blocked on this and nothing throws.
 */

/** Mirrors CoinTypeStore's initial value — the first render is always GC, so lobby_cc is the LCP image. */
const FIRST_RENDER_COIN_TYPE = 'GC';

const resolveBannerImageUrl = (banners: BannersData, animationEnabled: boolean): string | null => {
  const coinTypeBanner = FIRST_RENDER_COIN_TYPE === 'GC' ? banners.lobby_cc : banners.lobby_sc;

  // GenericHeader falls through to CasinoHeader unless both lobby banners are present, and
  // CasinoHeader carries its own artwork — nothing to warm here.
  if (!banners.lobby_cc || !banners.lobby_sc || !coinTypeBanner) {
    return null;
  }

  const isMobile = window.matchMedia(`(max-width: ${DESKTOP_THRESHOLD}px)`).matches;

  if (animationEnabled && lobbyBannerHasAnimation(coinTypeBanner, banners.lobby_animation_only)) {
    return getRiveBackgroundUrl(coinTypeBanner, isMobile) || null;
  }

  // Mirrors the <picture> in GenericHeader: a mobile <source> at the same breakpoint, else <img src>.
  return (isMobile && coinTypeBanner.mobile) || coinTypeBanner.desktop || null;
};

export const preloadLobbyBanner = (banners: BannersData, animationEnabled: boolean): void => {
  const href = resolveBannerImageUrl(banners, animationEnabled);

  if (!href) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'preload';
  // setAttribute rather than `link.as =`: the `as` IDL property is not reflected to the attribute
  // everywhere (jsdom included), and the attribute is what the preload actually keys on.
  link.setAttribute('as', 'image');
  // Matches fetchpriority on the <img>/<source> so the browser treats this as the same fetch
  // rather than scheduling a second, lower-priority one.
  link.setAttribute('fetchpriority', 'high');
  link.href = href;
  document.head.appendChild(link);
};
